<template>
  <div id="patient-lookup" class="lookupContainer mx-auto my-6">
    <!-- The screen changes under the agent while they are greeting a caller. -->
    <p class="sr-only" role="status">{{ liveStatus }}</p>

    <div v-if="state === 'loading'" class="statusBlock">
      <v-progress-circular color="primary" indeterminate size="40" />
      <p class="statusText">Looking up {{ displayPhone }}…</p>
    </div>

    <template v-else-if="state === 'redirecting' && only">
      <h2 class="lookupTitle my-2">Incoming call</h2>
      <p class="lookupSubtitle mb-3">{{ displayPhone }}</p>

      <div class="selectedPatientCard elevation-1">
        <div class="flex items-center">
          <v-avatar size="52" class="mr-4">
            <v-img :src="only.avatar_url" :alt="only.name">
              <template #placeholder>
                <v-icon size="52">mdi-account-circle</v-icon>
              </template>
            </v-img>
          </v-avatar>
          <div class="flex-1 min-w-0">
            <div class="patientName">{{ only.name }}</div>

            <div class="dobLine">
              <span class="dobLabel">DOB</span>
              <span class="dobValue">{{ dobLabel(only) }}</span>
            </div>

            <div class="patientInfo metaLine">
              <span v-if="only.chart_id">Chart ID: {{ only.chart_id }}</span>
              <span>Last visit: {{ lastVisitLabel(only) }}</span>
            </div>

            <div
              v-if="insuranceLine(only)"
              class="factLine"
              :class="{ selfPay: isSelfPay(only) }"
            >
              <v-icon size="16" class="mr-1">{{ insuranceIcon(only) }}</v-icon>
              <span>{{ insuranceLine(only) }}</span>
            </div>

            <div v-if="only.offices.length" class="factLine">
              <v-icon size="16" class="mr-1">mdi-map-marker</v-icon>
              <span>{{ only.offices.join(' · ') }}</span>
            </div>
          </div>
        </div>

        <v-progress-linear
          v-if="redirectRunning"
          class="mt-4"
          color="primary"
          height="4"
          :model-value="redirectProgress"
          rounded
        />

        <div class="flex items-center justify-between mt-3">
          <span class="patientInfo">{{ countdownLabel }}</span>
          <div class="flex items-center">
            <v-btn
              v-if="redirectRunning"
              class="mr-2"
              density="comfortable"
              variant="text"
              @click="cancelRedirect"
            >
              Cancel
            </v-btn>
            <v-btn color="primary" density="comfortable" variant="flat" @click="openChartNow">
              Open now
            </v-btn>
          </div>
        </div>
      </div>
    </template>

    <template v-else-if="state === 'choosing'">
      <h2 class="lookupTitle my-2">Who is calling?</h2>
      <p class="lookupSubtitle mb-3">{{ matchesLabel }}</p>

      <v-alert
        v-if="truncated"
        class="mb-3"
        density="compact"
        type="warning"
        variant="tonal"
      >
        This number is on more charts than can be listed — showing the
        {{ patients.length }} most recently seen.
      </v-alert>

      <!--
        Plain ul/li/a rather than v-list: a v-list-item is a listitem even when
        it is a link, and a row that opens a chart has to be announced as one.
      -->
      <ul class="patientsList elevation-1">
        <li v-for="patient in patients" :key="patient.uuid">
          <component
            :is="chartUrlFor(patient) ? 'a' : 'div'"
            class="patientItem"
            :href="chartUrlFor(patient) || undefined"
          >
            <v-avatar size="52" class="mr-4">
              <v-img :src="patient.avatar_url" :alt="patient.name">
                <template #placeholder>
                  <v-icon size="52">mdi-account-circle</v-icon>
                </template>
              </v-img>
            </v-avatar>

            <div class="flex-1 min-w-0">
              <div class="patientName flex items-center flex-wrap">
                <span>{{ patient.name }}</span>
                <span
                  v-if="patient.status && patient.status !== 'active'"
                  class="statusTag text-xs font-bold ml-2"
                >{{ patient.status }}</span>
              </div>

              <div class="dobLine">
                <span class="dobLabel">DOB</span>
                <span class="dobValue">{{ dobLabel(patient) }}</span>
              </div>

              <div class="patientInfo metaLine">
                <span v-if="patient.chart_id">Chart ID: {{ patient.chart_id }}</span>
                <span>Last visit: {{ lastVisitLabel(patient) }}</span>
              </div>

              <div
                v-if="insuranceLine(patient)"
                class="factLine"
                :class="{ selfPay: isSelfPay(patient) }"
              >
                <v-icon size="16" class="mr-1">{{ insuranceIcon(patient) }}</v-icon>
                <span>{{ insuranceLine(patient) }}</span>
              </div>

              <div v-if="patient.offices.length" class="factLine">
                <v-icon size="16" class="mr-1">mdi-map-marker</v-icon>
                <span>{{ patient.offices.join(' · ') }}</span>
              </div>

              <div v-if="!chartUrlFor(patient)" class="patientInfo">
                No DrChrono chart linked to this record
              </div>
            </div>
          </component>
        </li>
      </ul>
    </template>

    <div v-else class="noResultsMessage">
      <v-icon color="#666" size="32">mdi-account-search</v-icon>
      <p class="statusText">{{ emptyTitle }}</p>
      <p>{{ emptyHint }}</p>
      <v-btn class="mt-4" color="primary" variant="flat" :to="{ path: '/scheduler' }">
        Search patients manually
      </v-btn>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import type { PhoneMatch } from '@/store/modules/patientLookup';
import { chartUrl } from '@/services/drchrono.service';

type LookupState = 'loading' | 'redirecting' | 'choosing' | 'empty' | 'invalid' | 'internal' | 'failed';

// A US caller id is 10 digits, or 11 with the country code. Internal RingCentral
// calls arrive as a 3-4 digit extension, which belongs to no patient.
const MIN_PHONE_DIGITS = 10;
const MAX_PHONE_DIGITS = 11;

const REDIRECT_DELAY_MS = 3500;
const REDIRECT_TICK_MS = 100;

interface BaseData {
  state: LookupState;
  redirectRemaining: number;
  redirectTimer: number | undefined;
}

export default defineComponent({
  name: 'PatientLookup',

  data(): BaseData {
    return {
      state: 'loading',
      redirectRemaining: REDIRECT_DELAY_MS,
      redirectTimer: undefined,
    };
  },

  computed: {
    ...mapGetters({
      patients: 'phoneMatches',
      truncated: 'phoneMatchesTruncated',
      error: 'phoneLookupError',
    }),

    redirectRunning(): boolean {
      return this.redirectTimer !== undefined;
    },

    redirectSecondsLeft(): number {
      return Math.max(1, Math.ceil(this.redirectRemaining / 1000));
    },

    redirectProgress(): number {
      return 100 - (this.redirectRemaining / REDIRECT_DELAY_MS) * 100;
    },

    countdownLabel(): string {
      return this.redirectRunning
        ? `Opening chart in DrChrono in ${this.redirectSecondsLeft}s…`
        : 'Auto-open cancelled.';
    },

    phoneDigits(): string {
      return this.rawPhone.replace(/\D+/g, '');
    },

    isPatientPhone(): boolean {
      return this.phoneDigits.length >= MIN_PHONE_DIGITS
        && this.phoneDigits.length <= MAX_PHONE_DIGITS;
    },

    // RingCentral sends E.164 (+12125551234), which reads badly on screen.
    displayPhone(): string {
      const digits = this.phoneDigits.replace(/^1(?=\d{10}$)/, '');

      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
      }

      return this.rawPhone || '—';
    },

    // Trimmed because the router decodes the `+` of an E.164 number as a space.
    rawPhone(): string {
      const phone = this.$route.query.phone;

      return (Array.isArray(phone) ? String(phone[0] ?? '') : String(phone ?? '')).trim();
    },

    only(): PhoneMatch | undefined {
      return this.patients[0];
    },

    onlyChartUrl(): string | null {
      return this.only ? chartUrl(this.only.external_id) : null;
    },

    emptyTitle(): string {
      if (this.state === 'internal') return 'Internal call';
      if (this.state === 'invalid') return 'No phone number to look up';
      if (this.state === 'failed') return 'Lookup failed';

      return 'No patient found';
    },

    emptyHint(): string {
      if (this.state === 'internal') {
        return `${this.displayPhone} is an extension, not a patient number — this tab can be closed.`;
      }

      if (this.state === 'invalid') {
        return this.error?.message || 'Open this page with ?phone=… to look a caller up.';
      }

      if (this.state === 'failed') {
        return this.error?.message || 'Something went wrong reaching the server.';
      }

      return `Nobody in Prestige has ${this.displayPhone} on file.`;
    },

    matchesLabel(): string {
      return this.patients.length === 1
        ? `One chart matches ${this.displayPhone}`
        : `${this.patients.length} charts share ${this.displayPhone} — most recently seen first`;
    },

    // Read aloud on every state change; the visible layout says it in shapes.
    liveStatus(): string {
      if (this.state === 'loading') return `Looking up ${this.displayPhone}.`;
      if (this.state === 'redirecting') return `${this.only?.name}. Opening their chart in DrChrono.`;
      if (this.state === 'choosing') return `${this.matchesLabel}. Choose the caller.`;

      return `${this.emptyTitle}. ${this.emptyHint}`;
    },
  },

  watch: {
    // The softphone reuses the tab for the next call, changing only the query.
    '$route.query.phone': function onPhoneChange() {
      this.lookup();
    },
  },

  created() {
    this.lookup();
  },

  beforeUnmount() {
    this.cancelRedirect();
  },

  methods: {
    async lookup(): Promise<void> {
      this.cancelRedirect();

      if (!this.rawPhone.trim()) {
        this.state = 'invalid';

        return;
      }

      // No chart can be behind an extension, so close instead of making the
      // agent dismiss a dead end mid-greeting. The browser refuses to close a
      // tab it did not open by script, so the page still says why.
      if (!this.isPatientPhone) {
        this.state = 'internal';
        window.close();

        return;
      }

      this.state = 'loading';

      // false once a newer caller superseded this lookup, in which case the
      // store already belongs to them and this page must not act on it.
      const applied = await this.$store.dispatch('LOOKUP_PATIENTS_BY_PHONE', {
        phone: this.rawPhone,
      });

      if (!applied) {
        return;
      }

      if (this.error) {
        // 401 is handled globally by the api service.
        this.state = this.error.status === 422 ? 'invalid' : 'failed';

        return;
      }

      if (!this.patients.length) {
        this.state = 'empty';

        return;
      }

      // Never jump straight to a chart out of a list the server called
      // incomplete - the single match may not be the caller.
      if (!this.truncated && this.patients.length === 1 && this.onlyChartUrl) {
        this.state = 'redirecting';
        this.startRedirect();

        return;
      }

      this.state = 'choosing';
    },

    // Counted off the clock, not off the ticks: setInterval is throttled in a
    // background tab, and a drifting countdown either lies or never fires.
    startRedirect(): void {
      this.cancelRedirect();

      const deadline = Date.now() + REDIRECT_DELAY_MS;
      this.redirectRemaining = REDIRECT_DELAY_MS;

      this.redirectTimer = window.setInterval(() => {
        this.redirectRemaining = Math.max(0, deadline - Date.now());

        if (this.redirectRemaining === 0) {
          this.openChartNow();
        }
      }, REDIRECT_TICK_MS);
    },

    // A surviving timer would open the previous caller's chart mid-call.
    cancelRedirect(): void {
      if (this.redirectTimer !== undefined) {
        window.clearInterval(this.redirectTimer);
        this.redirectTimer = undefined;
      }
    },

    // `replace` so Back from DrChrono does not land here and redirect again.
    openChartNow(): void {
      this.cancelRedirect();

      if (this.onlyChartUrl) {
        window.location.replace(this.onlyChartUrl);
      }
    },

    chartUrlFor(patient: PhoneMatch): string | null {
      return chartUrl(patient.external_id);
    },

    // Empty when there is no payer on file, which hides the whole line: the
    // plan and member id qualify a payer and say nothing on their own.
    insuranceLine(patient: PhoneMatch): string {
      const insurance = patient.insurance;

      if (!insurance?.payer) {
        return '';
      }

      const plan = insurance.plan_name ? ` — ${insurance.plan_name}` : '';
      const member = insurance.member_id ? ` · Member ID ${insurance.member_id}` : '';

      return `${insurance.payer}${plan}${member}`;
    },

    isSelfPay(patient: PhoneMatch): boolean {
      return (patient.insurance?.payer ?? '').trim().toUpperCase() === 'SELF PAY';
    },

    insuranceIcon(patient: PhoneMatch): string {
      return this.isSelfPay(patient) ? 'mdi-cash' : 'mdi-shield-check';
    },

    // Age included: it separates parent from child on a household number.
    dobLabel(patient: PhoneMatch): string {
      if (!patient.date_of_birth) return 'not on file';

      const dob = this.$dayjs(patient.date_of_birth);

      if (!dob.isValid()) return 'not on file';

      return `${dob.format('MMM D, YYYY')} (${this.$dayjs().diff(dob, 'year')})`;
    },

    lastVisitLabel(patient: PhoneMatch): string {
      if (!patient.last_visit_at) return 'never seen';

      return this.$dayjs(patient.last_visit_at).format('MMM D, YYYY');
    },
  },
});
</script>

<style scoped>
.lookupContainer {
  width: 100%;
  max-width: 780px;
  background: #f9f9f9;
  border-radius: 8px;
  padding: 15px 10px;
  border: 1px solid #e0e0e0;
}

.lookupTitle {
  font-size: 18px;
  font-weight: 700;
  color: #111111;
}

.lookupSubtitle {
  font-size: 14px;
  color: #666666;
}

.patientsList {
  border-radius: 4px;
  background: white;
  list-style: none;
  margin: 0;
  padding: 0;
}

.patientItem {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  color: inherit;
  text-decoration: none;
}

.patientsList li:last-child .patientItem {
  border-bottom: none;
}

a.patientItem:hover {
  background: #f5f5f5;
}

a.patientItem:focus-visible {
  outline: 2px solid #086397;
  outline-offset: -2px;
}

.patientName {
  font-weight: 600;
  color: #086397;
  font-size: 14px;
}

.patientInfo {
  font-size: 14px;
  color: #666666;
}

/* The one field the agent reads back to confirm who is on the line. */
.dobLine {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-top: 2px;
  font-size: 14px;
  line-height: 20px;
}

.dobLabel {
  color: #666666;
}

.dobValue {
  font-weight: 600;
  color: #111111;
}

.metaLine {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-top: 2px;
}

.metaLine span:not(:last-child)::after {
  content: "·";
  margin-left: 8px;
  color: #999999;
}

.factLine {
  display: flex;
  align-items: center;
  margin-top: 4px;
  font-size: 14px;
  line-height: 20px;
  color: #234f65;
}

.factLine .v-icon {
  color: #6b8fa3;
}

.factLine.selfPay {
  color: #8a5a00;
}

.factLine.selfPay .v-icon {
  color: #b8860b;
}

.selectedPatientCard {
  background: white;
  border: 1px solid #d0d0d0;
  border-radius: 8px;
  padding: 12px;
}

.statusTag {
  padding: 2px 6px;
  border-radius: 6px;
  background: #e0e0e0;
  color: #444444;
  text-transform: capitalize;
}

.statusBlock,
.noResultsMessage {
  text-align: center;
  padding: 24px 20px;
  color: #666666;
  background: white;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}

.statusText {
  margin: 8px 0 0 0;
  font-size: 14px;
  color: #111111;
  font-weight: 600;
}

.noResultsMessage p {
  margin: 4px 0 0 0;
  font-size: 14px;
}
</style>
